import { useEffect, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { LOCALES, MULTI_LOCALE, changeLanguage } from '@/i18n'

interface LanguageSwitcherProps {
  open: boolean
  onClose: () => void
}

/**
 * 语言选择面板。
 *
 * 当前只配置了简体中文，MULTI_LOCALE 为 false 时整个组件（含入口按钮）不渲染；
 * 在 @/i18n 的 LOCALES 里加入第二门语言后自动生效，无需改动本文件。
 */
export default function LanguageSwitcher({ open, onClose }: LanguageSwitcherProps) {
  const { i18n } = useTranslation()
  const ref = useRef<HTMLDivElement>(null)

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    },
    [onClose]
  )

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose]
  )

  useEffect(() => {
    if (!open) return
    // 延后一帧再挂监听，避免触发打开的那次点击立刻把面板关掉
    const raf = requestAnimationFrame(() => {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleKeyDown)
    })
    return () => {
      cancelAnimationFrame(raf)
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, handleClickOutside, handleKeyDown])

  if (!MULTI_LOCALE || !open) return null

  const handleSelect = async (code: string) => {
    await changeLanguage(code)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[600] flex items-end justify-center bg-black/60">
      <div
        ref={ref}
        className="w-full max-w-md rounded-t-2xl px-4 pt-4 pb-8"
        style={{ background: '#0C0C1C', border: '1px solid rgba(28,25,48,1)' }}
      >
        {LOCALES.map((l) => {
          const active = i18n.language === l.code
          return (
            <button
              key={l.code}
              onClick={() => handleSelect(l.code)}
              className="w-full flex items-center justify-between px-3 py-3 rounded-xl mb-2"
              style={{
                background: active ? 'rgba(46,107,255,0.12)' : 'rgba(0,0,0,0.25)',
                border: `1px solid ${active ? 'rgba(46,107,255,0.35)' : 'transparent'}`,
              }}
            >
              <span className="text-sm" style={{ color: active ? '#2E6BFF' : '#FFFFFF' }}>
                {l.nativeLabel}
              </span>
              {active && (
                <span className="material-icons text-base" style={{ color: '#2E6BFF' }}>
                  check
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
